/**
 * PQL evaluator — THE leaf evaluator for filter rules.
 *
 * This is the single implementation of rule-matching semantics: the item
 * list's client engine (useItemListData.ts) delegates here, and the statement
 * bar evaluates full ASTs here, so builder mode and statement mode can never
 * drift apart. Semantics are the historical matchesRule() behavior, plus the
 * 'empty' operator (PRD §2.2 IS EMPTY).
 */

import { isCombinator, type FilterRule, type QueryNode } from './types.ts';

/** Resolves a field key to its value on the row being tested. */
export type FieldValueResolver = (field: string) => unknown;

export function evaluateRule(rule: FilterRule, getValue: FieldValueResolver): boolean {
  const val = getValue(rule.field);

  switch (rule.operator) {
    case 'in': {
      const acceptedValues: string[] = Array.isArray(rule.value)
        ? rule.value.map(String)
        : [String(rule.value)];
      // Support array-valued fields (e.g. cycleIds, trackIds, sessionIds)
      if (Array.isArray(val)) {
        return (val as unknown[]).some((v) => acceptedValues.includes(String(v)));
      }
      return acceptedValues.includes(String(val ?? ''));
    }
    case 'eq':
      return String(val ?? '') === String(rule.value);
    case 'boolean':
      return Boolean(val) === Boolean(rule.value);
    case 'text': {
      const needle = String(rule.value).toLowerCase();
      // Array-valued fields (e.g. tags) match when any element contains the needle.
      if (Array.isArray(val)) {
        return (val as unknown[]).some((v) => String(v ?? '').toLowerCase().includes(needle));
      }
      return String(val ?? '').toLowerCase().includes(needle);
    }
    case 'range': {
      const [min, max] = rule.value as [number | null, number | null];
      // null bounds appear when ±Infinity round-trips through JSON (saved reports).
      const loBound = typeof min === 'number' ? min : -Infinity;
      const hiBound = typeof max === 'number' ? max : Infinity;
      const num = Number(val);
      return num >= loBound && num <= hiBound;
    }
    case 'date-range': {
      const [start, end] = rule.value as [string, string];
      const rawDate = val instanceof Date ? val : val ? new Date(String(val)) : null;
      if (!rawDate || isNaN(rawDate.getTime())) return !start && !end;
      const ts = rawDate.getTime();
      const afterStart = !start || ts >= new Date(start).getTime();
      // Include the full end day by checking against end-of-day
      const beforeEnd = !end || ts <= new Date(end + 'T23:59:59.999').getTime();
      return afterStart && beforeEnd;
    }
    case 'empty': {
      const isEmpty = val == null || val === '' || (Array.isArray(val) && val.length === 0);
      return isEmpty === (rule.value !== false);
    }
    // Ordered comparisons deliberately fail for items with no value (NaN
    // comparisons are false) — `effort > 5` must not match items that have no
    // effort at all. Pure negation (NOT/!=) does include empties; see the PRD
    // §2.2 semantics note.
    case 'gt':
      return Number(val) > Number(rule.value);
    case 'lt':
      return Number(val) < Number(rule.value);
    case 'date-after': {
      const d = val instanceof Date ? val : val ? new Date(String(val)) : null;
      if (!d || isNaN(d.getTime())) return false;
      return d.getTime() > new Date(String(rule.value) + 'T23:59:59.999').getTime();
    }
    case 'date-before': {
      const d = val instanceof Date ? val : val ? new Date(String(val)) : null;
      if (!d || isNaN(d.getTime())) return false;
      return d.getTime() < new Date(String(rule.value)).getTime();
    }
    default:
      return true;
  }
}

/** Evaluate a full AST against one row. A null node matches everything. */
export function evaluateNode(node: QueryNode | null, getValue: FieldValueResolver): boolean {
  if (node == null) return true;
  if (!isCombinator(node)) return evaluateRule(node, getValue);
  switch (node.op) {
    case 'and':
      return node.children.every((c: QueryNode) => evaluateNode(c, getValue));
    case 'or':
      return node.children.length === 0 || node.children.some((c: QueryNode) => evaluateNode(c, getValue));
    case 'not':
      return !evaluateNode(node.child, getValue);
    default:
      return true;
  }
}
